import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import SubmarineIcon from './SubmarineIcon'
import {
  selectAgentModelOptions,
  selectDeepDiveOption,
  selectEffortLevelOptions,
  selectImageModelOptions,
  selectModelRoles,
} from '../../../features/openai/openaiSlice'

type ModelOption = { key: string; label: string }

type AgentSettingsMenuProps = {
  effortLevel: number
  agentModel: string
  imageModel: string
  onSelectEffort: (level: number) => void
  onSelectModel: (key: string) => void
  onSelectImageModel: (key: string) => void
  /** Which edge of the trigger the panel is anchored to. */
  align?: 'left' | 'right'
}

type PickerId = 'model' | 'imageModel'

const labelFor = (options: readonly ModelOption[], value: string, fallback: string) =>
  options.find(({ key }) => key === value)?.label ?? fallback

/** Main-view row that drills into an option picker. */
const PickerRow = ({
  label,
  value,
  onOpen,
}: {
  label: string
  value: string
  onOpen: () => void
}) => (
  <button
    type="button"
    onClick={onOpen}
    className="tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-1.5 tw-rounded-lg tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-left tw-transition-colors hover:tw-bg-gray-50"
  >
    <span className="tw-text-xs tw-text-gray-600">{label}</span>
    <span className="tw-flex tw-items-center tw-gap-0.5 tw-min-w-0">
      <span className="tw-text-xs tw-font-medium tw-text-gray-900 tw-truncate">{value}</span>
      <ChevronRight className="tw-w-3.5 tw-h-3.5 tw-text-gray-400 tw-flex-shrink-0" />
    </span>
  </button>
)

/** Sub-view listing the options for one setting. */
const PickerView = ({
  title,
  options,
  value,
  onSelect,
  onBack,
}: {
  title: string
  options: readonly ModelOption[]
  value: string
  onSelect: (key: string) => void
  onBack: () => void
}) => (
  <div>
    <button
      type="button"
      onClick={onBack}
      className="tw-w-full tw-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-1 tw-mb-1 tw-rounded-lg tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors hover:tw-bg-gray-50"
    >
      <ChevronLeft className="tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
      <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-400">
        {title}
      </span>
    </button>
    <div role="listbox" aria-label={title}>
      {options.map(({ key, label }) => {
        const selected = key === value
        return (
          <button
            key={key}
            type="button"
            role="option"
            aria-selected={selected}
            onClick={() => onSelect(key)}
            className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-1.5 tw-rounded-lg tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-left tw-transition-colors hover:tw-bg-gray-50 ${
              selected ? 'tw-text-gray-900 tw-font-medium' : 'tw-text-gray-600'
            }`}
          >
            <span className="tw-text-xs tw-truncate">{label}</span>
            {selected && <Check className="tw-w-3.5 tw-h-3.5 tw-text-blue-600 tw-flex-shrink-0" />}
          </button>
        )
      })}
    </div>
  </div>
)

/**
 * Compact agent settings popover (effort + model + image model).
 *
 * Model pickers drill into an in-panel sub-view rather than a nested dropdown:
 * a portalled menu would register as an outside click and dismiss this popover,
 * and an absolutely positioned one would be clipped by the panel's `max-h`.
 */
const AgentSettingsMenu = ({
  effortLevel,
  agentModel,
  imageModel,
  onSelectEffort,
  onSelectModel,
  onSelectImageModel,
  align = 'left',
}: AgentSettingsMenuProps) => {
  const [picker, setPicker] = useState<PickerId | null>(null)

  // Options come from the papaya catalog in redux (see openaiSlice), so a new or
  // retired model shows up here without a client release.
  const agentModelOptions = useSelector(selectAgentModelOptions)
  const imageModelOptions = useSelector(selectImageModelOptions)
  const effortLevelOptions = useSelector(selectEffortLevelOptions)
  const deepDiveOption = useSelector(selectDeepDiveOption)
  const modelRoles = useSelector(selectModelRoles)

  // Deep Dive is located by the catalog level, never a hardcoded 6, and the
  // whole effort block is hidden until the server defines the scale.
  const isDeepDive = deepDiveOption != null && effortLevel === deepDiveOption.level
  const activeEffort = isDeepDive
    ? deepDiveOption
    : effortLevelOptions.find(({ level }) => level === effortLevel) ?? effortLevelOptions[0]

  // Resolve role keys (fast/frontier/…) to whatever the server currently has in
  // those slots, so the hint names real models without this file knowing any.
  const roleLabel = (roleKey: string) => labelFor(agentModelOptions, roleKey, roleKey)
  const autoRoutingHint = {
    deepDive: modelRoles
      ? `Deep Dive: ${roleLabel(modelRoles.deepDiveLead)} directs and reviews; ${roleLabel(modelRoles.deepDiveExecution)} executes.`
      : 'Deep Dive: one frontier model directs and reviews while another executes.',
    standard: modelRoles
      ? `Picks ${roleLabel(modelRoles.fast)}–${roleLabel(modelRoles.high)} from the question; Standard won’t force the top tier.`
      : 'Picks a model from the question; Standard won’t force the top tier.',
  }

  const panelClassName = `tw-absolute tw-bottom-full tw-mb-2 ${
    align === 'right' ? 'tw-right-0' : 'tw-left-0'
  } tw-w-[264px] tw-max-w-[calc(100vw-1.5rem)] tw-max-h-[min(60vh,24rem)] tw-overflow-y-auto tw-bg-white tw-rounded-xl tw-shadow-lg tw-border tw-border-gray-200 tw-z-50 tw-p-2`

  if (picker) {
    const isModel = picker === 'model'
    return (
      <div className={panelClassName}>
        <PickerView
          title={isModel ? 'Model' : 'Image model'}
          options={isModel ? agentModelOptions : imageModelOptions}
          value={isModel ? agentModel : imageModel}
          onSelect={(key) => {
            if (isModel) onSelectModel(key)
            else onSelectImageModel(key)
            setPicker(null)
          }}
          onBack={() => setPicker(null)}
        />
      </div>
    )
  }

  return (
    <div className={panelClassName}>
      {/* No local effort scale exists, so the whole block waits on the catalog
          rather than rendering an empty segmented control. */}
      {effortLevelOptions.length > 0 && (
      <>
      <div className="tw-flex tw-items-baseline tw-justify-between tw-px-0.5 tw-mb-1.5">
        <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-400">
          Effort
        </span>
        <span
          className={`tw-text-xs tw-font-semibold ${
            isDeepDive
              ? 'tw-bg-gradient-to-r tw-from-amber-500 tw-via-fuchsia-500 tw-to-blue-500 tw-bg-clip-text tw-text-transparent'
              : 'tw-font-medium tw-text-gray-700'
          }`}
        >
          {activeEffort?.label ?? ''}
        </span>
      </div>
      <div className="tw-flex tw-bg-gray-100 tw-rounded-lg tw-p-0.5">
        {effortLevelOptions.map(({ level, label }) => (
          <button
            key={level}
            type="button"
            aria-pressed={effortLevel === level}
            onClick={() => onSelectEffort(level)}
            className={`tw-flex-1 tw-px-1 tw-py-1 tw-text-[11px] tw-font-medium tw-rounded-md tw-transition-colors tw-duration-150 tw-border-0 tw-cursor-pointer ${
              effortLevel === level
                ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm'
                : 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {/* Deep Dive — orchestrated mode, rendered only when the server offers it. */}
      {deepDiveOption && (
      <div
        className={`tw-mt-1.5 tw-rounded-lg tw-p-[1.5px] tw-bg-gradient-to-r tw-from-amber-400 tw-via-fuchsia-500 tw-to-blue-500 ${
          isDeepDive ? '' : 'tw-opacity-70 hover:tw-opacity-100 tw-transition-opacity'
        }`}
      >
        <button
          type="button"
          aria-pressed={isDeepDive}
          onClick={() => deepDiveOption && onSelectEffort(deepDiveOption.level)}
          className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-1.5 tw-rounded-[7px] tw-border-0 tw-cursor-pointer tw-text-left tw-transition-colors ${
            isDeepDive ? 'tw-bg-fuchsia-50' : 'tw-bg-white hover:tw-bg-gray-50'
          }`}
        >
          <span className="tw-flex tw-items-center tw-gap-1.5">
            <SubmarineIcon className="tw-w-3.5 tw-h-3.5 tw-text-fuchsia-500" />
            <span className="tw-text-xs tw-font-semibold tw-bg-gradient-to-r tw-from-amber-500 tw-via-fuchsia-500 tw-to-blue-500 tw-bg-clip-text tw-text-transparent">
              Deep Dive
            </span>
          </span>
          {isDeepDive && <Check className="tw-w-3.5 tw-h-3.5 tw-text-fuchsia-600 tw-flex-shrink-0" />}
        </button>
      </div>
      )}
      <p className="tw-text-[11px] tw-leading-snug tw-text-gray-500 tw-mt-1.5 tw-mb-0 tw-px-0.5">
        {activeEffort?.description ?? ''}
      </p>
      </>
      )}

      <div className="tw-border-t tw-border-gray-100 tw-my-2" />

      <PickerRow
        label="Model"
        value={labelFor(agentModelOptions, agentModel, agentModel)}
        onOpen={() => setPicker('model')}
      />
      {agentModel === 'auto' && (
        <p className="tw-text-[10px] tw-leading-snug tw-text-gray-400 tw-mt-0.5 tw-mb-0 tw-px-2">
          {/* Model names come from the catalog's role map, never hardcoded — this
              copy used to read "GPT-6 Astra directs…" and would have gone stale
              the moment the lineup moved. */}
          {isDeepDive
            ? autoRoutingHint.deepDive
            : autoRoutingHint.standard}
        </p>
      )}
      <PickerRow
        label="Image model"
        value={labelFor(imageModelOptions, imageModel, imageModel)}
        onOpen={() => setPicker('imageModel')}
      />

      <p className="tw-text-[10px] tw-leading-snug tw-text-gray-400 tw-mt-2 tw-mb-0 tw-px-0.5">
        The Agent always asks you to confirm before any change.
      </p>
    </div>
  )
}

export default AgentSettingsMenu
